import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppLifecycleService } from '../platform/app-lifecycle.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

/**
 * Signs the user out when their session has expired while the app was
 * backgrounded.
 *
 * `sessionExpiryInterceptor` only reacts once a request has already failed,
 * which on a resumed app means the user watches several screens error out
 * before anything happens. Checking on resume catches it first: a phone left
 * in a pocket overnight lands on the sign-in screen rather than on a
 * dashboard that cannot load.
 *
 * Deliberately not a timer. Scheduling a sign-out for the exact expiry
 * instant would fire while the app is suspended and the WebView is frozen, so
 * it would be unreliable anyway — and it would log people out mid-shift while
 * they were looking at the screen.
 */
@Injectable({ providedIn: 'root' })
export class SessionWatcherService {
  private readonly auth = inject(AuthService);
  private readonly lifecycle = inject(AppLifecycleService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  private initialised = false;

  init(): void {
    if (this.initialised) return;
    this.initialised = true;

    this.lifecycle.resumed$.subscribe(() => this.check());
  }

  private check(): void {
    // `isTokenExpired()` is false when the API gave no expiry, so this never
    // signs out a session we cannot prove is dead.
    if (!this.auth.accessToken || !this.auth.isTokenExpired()) return;

    this.auth.clearSession();
    this.toast.error('Your session has expired. Please sign in again.');
    void this.router.navigate(['/login']);
  }
}
